import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Menu, Globe } from 'lucide-react';
import QuoteModal from './modals/QuoteModal';
import logo from '../assets/images/Logo_Probuild_clear.png';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguage();

  const navItems = [
    { label: t('nav.solutions'), href: '#solutions', key: 'solutions' },
    { label: t('nav.materials'), href: '#materials', key: 'materials' },
    { label: t('nav.about'), href: '#about', key: 'about' },
    { label: t('nav.partners'), href: '#partners', key: 'partners' },
    { label: t('nav.contact'), href: '#contact', key: 'contact' },
  ];

  return (
    <header className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img 
              src={logo}
              alt="PROBUILD MARKET" 
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Language Selector & CTA */}
          <div className="hidden md:flex items-center space-x-3">
            {/* Language Selector Dropdown */}
            <Select value={language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-[120px]">
                <Globe size={16} className="mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              onClick={() => setIsQuoteModalOpen(true)}
              className="bg-gray-900 hover:bg-gray-800 text-white px-6"
            >
              {t('nav.getQuote')}
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="sm">
                <Menu size={20} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <nav className="flex flex-col space-y-4 mt-8">
                {navItems.map((item) => (
                  <a
                    key={item.key}
                    href={item.href}
                    className="text-lg font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
                <div className="mt-4 mb-4">
                  {/* Language Selector Dropdown for Mobile */}
                  <Select value={language} onValueChange={changeLanguage}>
                    <SelectTrigger>
                      <Globe size={16} className="mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="fr">Français</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button 
                  onClick={() => {
                    setIsQuoteModalOpen(true);
                    setIsMenuOpen(false);
                  }}
                  className="bg-gray-900 hover:bg-gray-800 text-white mt-6"
                >
                  {t('nav.getQuote')}
                </Button>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      
      <QuoteModal 
        isOpen={isQuoteModalOpen} 
        onClose={() => setIsQuoteModalOpen(false)} 
      />
    </header>
  );
};

export default Header;